/***************************************************************************************
 * FileName      : HotspotTagService.ts
 * Description   : Matterport 핫스팟 태그 배치/제거 서비스 (구성요소 부품 클릭 시 해당 태그 표시)
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.02.20
 * Modified Date :
 * Reference     : matterport_mvp1/src/11-tag-placement, assets/json/matterport/hotspot/*.json
 ***************************************************************************************/

// 2026.02.20 : 박은정 : 핫스팟 태그 JSON 데이터 로드
import heatExchangerHotspots from '../../assets/json/matterport/hotspot/heat-exchanger.json';
import distillationHotspots from '../../assets/json/matterport/hotspot/distillation.json';
import circulationHotspots from '../../assets/json/matterport/hotspot/circulation.json';

// ============================================================================
// 타입 정의
// ============================================================================

/** 핫스팟 태그 데이터 인터페이스 (JSON 파일 1건 = 태그 1건) */
export interface HotspotTagData {
  id: string;
  deviceId: string;
  label: string;
  anchorPosition: { x: number; y: number; z: number };
  stemVector: { x: number; y: number; z: number };
  stemHeight: number;
  color: { r: number; g: number; b: number };
  // 2026.04.23 : 박은정 : 3D 테두리 하이라이트용 박스 크기/오프셋 (optional)
  boxSize?: { x: number; y: number; z: number };
  boxOffset?: { x: number; y: number; z: number };
}

// ============================================================================
// 핫스팟 데이터 맵 (equipmentId → deviceId → HotspotTagData)
// ============================================================================

// 2026.02.20 : 박은정 : 장비별 핫스팟 태그를 deviceId 기준 Map으로 구성
const hotspotMap: Record<string, Record<string, HotspotTagData>> = {};

/**
 * 핫스팟 JSON 배열을 equipmentId 키로 등록
 */
function registerHotspots(equipmentId: string, tags: HotspotTagData[]): void {
  const deviceMap: Record<string, HotspotTagData> = {};
  for (const tag of tags) {
    deviceMap[tag.deviceId] = tag;
  }
  hotspotMap[equipmentId] = deviceMap;
}

// 2026.02.20 : 박은정 : 3개 장비 핫스팟 데이터 등록
registerHotspots('heat-exchanger', heatExchangerHotspots as unknown as HotspotTagData[]);
registerHotspots('distillation', distillationHotspots as unknown as HotspotTagData[]);
registerHotspots('circulation', circulationHotspots as unknown as HotspotTagData[]);

// ============================================================================
// HotspotTagService 클래스
// ============================================================================

/**
 * 핫스팟 태그 서비스
 * - Matterport SDK를 이용하여 핫스팟 태그를 동적으로 배치/제거
 * - 구성요소 부품 클릭 시 해당 장비의 핫스팟 태그 1개를 토글(표시/숨김)
 */
export class HotspotTagService {
  private static instance: HotspotTagService;
  private sdk: any = null;
  /** 현재 배치된 태그: deviceId → SDK가 반환한 실제 태그 ID */
  private placedTags: Map<string, string> = new Map();

  // 2026.02.20 : 박은정 : 싱글톤 패턴
  static getInstance(): HotspotTagService {
    if (!HotspotTagService.instance) {
      HotspotTagService.instance = new HotspotTagService();
    }
    return HotspotTagService.instance;
  }

  /**
   * 2026.02.20 : 박은정 : SDK 인스턴스 초기화
   */
  initialize(sdk: any): void {
    this.sdk = sdk;
  }

  /**
   * SDK 초기화 여부 확인
   */
  isInitialized(): boolean {
    return this.sdk !== null;
  }

  /**
   * 2026.02.21 : 박은정 : 특정 장비의 특정 디바이스 핫스팟 태그 배치
   */
  async addTag(equipmentId: string, deviceId: string): Promise<boolean> {
    if (!this.sdk) {
      console.warn('[HotspotTagService] SDK가 초기화되지 않았습니다.');
      return false;
    }

    const tagData = this.getTagData(equipmentId, deviceId);
    if (!tagData) {
      console.warn(`[HotspotTagService] 핫스팟 데이터 없음: equipmentId=${equipmentId}, deviceId=${deviceId}`);
      return false;
    }

    // 이미 배치된 경우 중복 배치 방지
    if (this.placedTags.has(deviceId)) return true;

    try {
      // 2026.02.21 : 박은정 : Matterport Tag API로 태그 배치
      // Tag.add()는 string[] 반환 — SDK가 생성한 실제 태그 ID를 저장해야 remove 시 사용 가능
      const returnedIds: string[] = await this.sdk.Tag.add({
        label: tagData.label,
        description: '',
        anchorPosition: tagData.anchorPosition,
        stemVector: tagData.stemVector,
        color: tagData.color,
      });

      if (returnedIds && returnedIds.length > 0) {
        this.placedTags.set(deviceId, returnedIds[0]);
        console.log(`[HotspotTagService] 태그 배치 완료: deviceId=${deviceId}, sdkTagId=${returnedIds[0]}`);
      }
      return true;
    } catch (error) {
      console.error(`[HotspotTagService] 태그 배치 실패: deviceId=${deviceId}`, error);
      return false;
    }
  }

  /**
   * 2026.02.21 : 박은정 : 특정 디바이스 핫스팟 태그 제거
   */
  async removeTag(_equipmentId: string, deviceId: string): Promise<boolean> {
    if (!this.sdk) return false;

    const sdkTagId = this.placedTags.get(deviceId);
    if (!sdkTagId) return true;

    try {
      await this.sdk.Tag.remove(sdkTagId);
      this.placedTags.delete(deviceId);
      return true;
    } catch (error) {
      console.error(`[HotspotTagService] 태그 제거 실패: deviceId=${deviceId}`, error);
      this.placedTags.delete(deviceId);
      return false;
    }
  }

  /**
   * 2026.02.21 : 박은정 : 현재 배치된 모든 핫스팟 태그 제거
   */
  async removeAllTags(): Promise<void> {
    if (!this.sdk) return;

    // 2026.02.21 : 박은정 : SDK가 반환한 실제 태그 ID로 제거
    for (const [deviceId, sdkTagId] of this.placedTags.entries()) {
      try {
        await this.sdk.Tag.remove(sdkTagId);
      } catch (e) {
        console.warn(`[HotspotTagService] 태그 제거 실패 (일괄): deviceId=${deviceId}`, e);
      }
    }

    this.placedTags.clear();
  }

  /**
   * 2026.02.21 : 박은정 : 특정 디바이스 태그 배치 여부 확인
   */
  isTagPlaced(deviceId: string): boolean {
    return this.placedTags.has(deviceId);
  }

  /**
   * 2026.02.20 : 박은정 : 특정 장비의 특정 디바이스 핫스팟 데이터 조회
   */
  getTagData(equipmentId: string, deviceId: string): HotspotTagData | null {
    const deviceMap = hotspotMap[equipmentId];
    if (!deviceMap) return null;
    return deviceMap[deviceId] || null;
  }

  /**
   * 2026.02.20 : 박은정 : 특정 장비에 핫스팟 데이터가 있는 디바이스 ID 목록 조회
   */
  getAvailableDeviceIds(equipmentId: string): string[] {
    const deviceMap = hotspotMap[equipmentId];
    if (!deviceMap) return [];
    return Object.keys(deviceMap);
  }

  /**
   * 2026.02.21 : 박은정 : 현재 배치된 태그 수 조회
   */
  getPlacedTagCount(): number {
    return this.placedTags.size;
  }
}

